import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sprout, Leaf, ScrollText, Scale, ArrowRight, Plus, FileText, Sparkles } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import StagePill from "@/components/StagePill";
import VeggieIcon from "@/components/VeggieIcon";
import WeatherStrip from "@/components/WeatherStrip";
import DemoBanner from "@/components/DemoBanner";
import { Button } from "@/components/ui/button";
import { getDashboard, seasonReportUrl } from "@/lib/api";
import { useGardens } from "@/contexts/GardenContext";
import { useAuth } from "@/contexts/AuthContext";

const Stat = ({ label, value, hint }) => (
    <div className="journal-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="overline text-[var(--vm-sage)] mb-2">{label}</div>
        <div className="font-serif-display text-4xl text-[var(--vm-text)]">{value}</div>
        {hint && <div className="text-xs text-[var(--vm-text-muted)] mt-1">{hint}</div>}
    </div>
);

export default function Dashboard() {
    const { activeGardenId } = useGardens();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        getDashboard(activeGardenId || undefined)
            .then(setData)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [activeGardenId]);

    if (loading) {
        return (
            <div className="text-[var(--vm-text-muted)] text-base" data-testid="dashboard-loading">
                Tending to the garden…
            </div>
        );
    }
    if (error) {
        return <div className="text-[var(--vm-secondary)]" data-testid="dashboard-error">Couldn't load dashboard: {error}</div>;
    }

    const stats = data?.stats || {};
    const upcoming = data?.upcoming || [];
    const gardenId = data?.garden?.id;
    const isPlus = user?.tier === "plus";
    const pdfUrl = gardenId ? seasonReportUrl(gardenId) : null;

    return (
        <div className="space-y-12" data-testid="dashboard-page">
            <DemoBanner />
            <PageHeader
                overline="Today in the garden"
                title={data?.garden?.name || "My Garden"}
                description={`${data?.garden?.location || "Ireland"} · ${data?.garden?.climate || "temperate maritime"}. Plan, sow, watch the sky, harvest.`}
                action={
                    <Link to="/chat">
                        <Button data-testid="open-chat-cta" className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-6 py-5">
                            <Leaf className="w-4 h-4 mr-2" /> Ask rooteed
                        </Button>
                    </Link>
                }
            />

            <WeatherStrip weather={data?.weather} />

            <section className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <Stat label="Beds" value={stats.bed_count ?? 0} hint="raised, greenhouse, container" />
                <Stat label="Crops" value={stats.crop_count ?? 0} hint="across all stages" />
                <Stat
                    label="Expected Yield"
                    value={`${stats.expected_yield_low_kg ?? 0}–${stats.expected_yield_high_kg ?? 0}`}
                    hint="kg this season"
                />
                <Stat label="Harvested" value={`${stats.harvested_kg ?? 0} kg`} hint="logged so far" />
            </section>

            {/* Stage breakdown */}
            <section>
                <div className="overline text-[var(--vm-sage)] mb-3">Crop stages</div>
                <div className="flex flex-wrap gap-2" data-testid="stage-breakdown">
                    {Object.entries(stats.stage_counts || {}).map(([s, count]) => (
                        <div key={s} className="flex items-center gap-2 journal-card px-4 py-2">
                            <StagePill stage={s} />
                            <span className="text-sm text-[var(--vm-text-secondary)]">{count}</span>
                        </div>
                    ))}
                    {Object.keys(stats.stage_counts || {}).length === 0 && (
                        <div className="text-[var(--vm-text-muted)] text-sm">No crops yet — head to <Link to="/crops" className="underline text-[var(--vm-accent)]">Crops</Link> to plant your first.</div>
                    )}
                </div>
            </section>

            {/* Upcoming */}
            <section>
                <div className="flex items-end justify-between mb-4">
                    <div>
                        <div className="overline text-[var(--vm-sage)] mb-1">Next 14 days</div>
                        <h2 className="font-serif-display text-3xl text-[var(--vm-text)]">Upcoming Tasks</h2>
                    </div>
                    <Link to="/timeline" className="text-sm text-[var(--vm-accent)] hover:underline flex items-center gap-1" data-testid="upcoming-see-all">
                        Full timeline <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
                {upcoming.length === 0 ? (
                    <div className="journal-card p-8 text-center">
                        <ScrollText className="w-8 h-8 mx-auto text-[var(--vm-sage)] mb-3" strokeWidth={1.5} />
                        <p className="text-[var(--vm-text-secondary)]">No tasks in the next two weeks. Plan your next sowing in <Link to="/crops" className="underline text-[var(--vm-accent)]">Crops</Link>.</p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-4" data-testid="upcoming-tasks">
                        {upcoming.map((u) => (
                            <div key={`${u.crop_id}-${u.action}-${u.date}`} className="journal-card p-5 flex items-center justify-between" data-testid={`upcoming-${u.crop_id}-${u.action}`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-11 h-11 rounded-lg bg-[var(--vm-input)]/50 border border-[var(--vm-border)] flex items-center justify-center shrink-0">
                                        <VeggieIcon cropName={u.crop_name} stage={u.action === "harvest" ? "harvest" : "vegetative"} size={32} />
                                    </div>
                                    <div>
                                        <div className="overline text-[var(--vm-sage)] text-[10px]">{u.action}</div>
                                        <div className="font-serif-display text-xl mt-0.5 text-[var(--vm-text)]">{u.crop_name}</div>
                                        <div className="text-xs text-[var(--vm-text-muted)] mt-1">{u.date} · in {u.days_away}d</div>
                                    </div>
                                </div>
                                <Sprout className="w-5 h-5 text-[var(--vm-accent)]" strokeWidth={1.5} />
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Quick links */}
            <section className="grid md:grid-cols-3 gap-6">
                <Link to="/beds" className="journal-card p-6 group" data-testid="quick-link-beds">
                    <Plus className="w-5 h-5 text-[var(--vm-accent)] mb-3" strokeWidth={1.5} />
                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">Add a bed</h3>
                    <p className="text-sm text-[var(--vm-text-secondary)] mt-1">Define a raised bed, greenhouse or container.</p>
                </Link>
                <Link to="/import" className="journal-card p-6 group" data-testid="quick-link-import">
                    <Scale className="w-5 h-5 text-[var(--vm-accent)] mb-3" strokeWidth={1.5} />
                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">Import a CSV</h3>
                    <p className="text-sm text-[var(--vm-text-secondary)] mt-1">From VegPlotter, Garden Planner, or your own.</p>
                </Link>
                <Link to="/book-notes" className="journal-card p-6 group" data-testid="quick-link-booknotes">
                    <ScrollText className="w-5 h-5 text-[var(--vm-accent)] mb-3" strokeWidth={1.5} />
                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">Apply book notes</h3>
                    <p className="text-sm text-[var(--vm-text-secondary)] mt-1">Paste notes from your favourite gardening book.</p>
                </Link>
            </section>

            {/* Season PDF export */}
            {gardenId && (
                <section className="journal-card p-6 flex flex-wrap items-center gap-4" data-testid="season-report-section">
                    <FileText className="w-8 h-8 text-[var(--vm-accent)]" strokeWidth={1.5} />
                    <div className="flex-1 min-w-[12rem]">
                        <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">Season report</h3>
                        <p className="text-sm text-[var(--vm-text-muted)] mt-1">
                            A printable journal of this garden's beds, crops, and harvest log.
                            {!isPlus && " Available on rooteed Plus."}
                        </p>
                    </div>
                    {isPlus ? (
                        <Button
                            asChild
                            data-testid="season-report-download-button"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                        >
                            <a href={pdfUrl} target="_blank" rel="noreferrer">
                                <FileText className="w-4 h-4 mr-2" /> Download PDF
                            </a>
                        </Button>
                    ) : (
                        <Button
                            asChild
                            data-testid="season-report-upgrade-button"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                        >
                            <Link to="/pricing">
                                <Sparkles className="w-4 h-4 mr-2" /> Unlock with Plus
                            </Link>
                        </Button>
                    )}
                </section>
            )}
        </div>
    );
}
